import { useState } from 'react';
import Button from 'src/components/Button';
import Modal, { ModalFooter } from 'src/components/Modal';
import TextAreaField from 'src/components/TextAreaField';

import { useRejectInvoiceMutation } from './billingApi';

type RejectInvoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  rejectedSessions: Set<string>;
  onSuccess: () => void;
};

export const RejectInvoiceModal = ({
  isOpen,
  onClose,
  invoiceId,
  rejectedSessions,
  onSuccess,
}: RejectInvoiceModalProps) => {
  const [rejectReason, setRejectReason] = useState('');
  const [rejectInvoice, { isLoading: isRejectLoading }] = useRejectInvoiceMutation();

  const onConfirmReject = async () => {
    if (invoiceId && rejectedSessions.size > 0 && rejectReason.length <= 1000) {
      try {
        await rejectInvoice({
          invoiceId,
          sessionIds: Array.from(rejectedSessions),
          reason: rejectReason,
        }).unwrap();
        setRejectReason('');
        onSuccess();
      } catch (error) {
        console.error('Error rejecting invoice:', error);
      }
    }
  };

  const handleClose = () => {
    setRejectReason('');
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleClose}
      title="Reject invoice"
      className="w-full max-w-xs rounded-lg md:max-w-2xl"
    >
      <div>
        <p className="text-neutral-300 typography-loud-sm">
          Please provide the reason for invoice rejection below.
        </p>
        <p className="pb-6 text-neutral-300 typography-loud-sm">
          Note that the invoice will be returned to the provider for revision and resubmission.
        </p>
        <p className="pb-4 text-neutral-100 typography-loud-sm">
          Rejected sessions: <b>{rejectedSessions.size}</b>
        </p>
        <TextAreaField
          placeholder="Type the reason for invoice rejection."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          maxLength={1000}
          showCharacterCount
          rows={4}
          error={
            rejectReason.length > 1000
              ? 'The reason for rejection must not exceed 1000 characters'
              : undefined
          }
        />
      </div>
      <ModalFooter className="pb-6">
        <Button
          preset="secondary"
          onClick={onConfirmReject}
          isLoading={isRejectLoading}
          disabled={rejectReason.length > 1000 || rejectedSessions.size === 0}
        >
          Reject Invoice
        </Button>
        <Button preset="tertiary" onClick={handleClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
